import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, ArrowRight, Tag, Hash } from "lucide-react";
import { apiRequest } from "../../config/api";
import { debounce, getImageUrl } from "../../utils/helpers";
import CircularLoader from "../common/CircularLoader";

// Enhanced CSS animations with theme colors
const searchBarStyles = `
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  @keyframes shimmer {
    0% {
      background-position: -200px 0;
    }
    100% {
      background-position: calc(200px + 100%) 0;
    }
  }
  
  .search-suggestion-enter {
    animation: slideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .search-input-focus {
    animation: fadeIn 0.2s ease-out;
  }
  
  .search-loading-shimmer {
    background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
    background-size: 200px 100%;
    animation: shimmer 1.5s infinite;
  }
  
  .search-glow {
    box-shadow: 0 0 0 3px rgba(236, 72, 153, 0.1), 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
  
  .search-glow:focus-within {
    box-shadow: 0 0 0 3px rgba(236, 72, 153, 0.2), 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }
`;

const SearchBar = ({
  value,
  onChange,
  placeholder = "Search for yarn, needles, patterns...",
  className = "",
  showSuggestions = true,
  onSuggestionSelect,
  onSearchSubmit,
}) => {
  const [localValue, setLocalValue] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestionsDropdown, setShowSuggestionsDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState(null);
  const [isFocused, setIsFocused] = useState(false);

  const navigate = useNavigate();
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Debounced search function
  const debouncedSearch = useMemo(
    () =>
      debounce(async (searchTerm) => {
        if (!searchTerm.trim() || searchTerm.length < 1) {
          setSuggestions([]);
          setShowSuggestionsDropdown(false);
          return;
        }

        console.log("🔍 Searching for:", searchTerm);

        try {
          setLoading(true);
          setError(null);

          const response = await apiRequest(
            `/products/search-suggestions?q=${encodeURIComponent(
              searchTerm
            )}&limit=10`
          );

          if (response.success) {
            console.log("Search suggestions data:", response.data);
            console.log(
              "🔍 Found products:",
              response.data.map((p) => p.name)
            );
            setSuggestions(response.data);
            setShowSuggestionsDropdown(response.data.length > 0);
          } else {
            setSuggestions([]);
            setShowSuggestionsDropdown(false);
          }
        } catch (err) {
          console.error("Search suggestions error:", err);
          setError("Failed to load suggestions");
          setSuggestions([]);
          setShowSuggestionsDropdown(false);
        } finally {
          setLoading(false);
        }
      }, 300),
    []
  );

  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  const handleChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
      onChange(newValue);

      if (showSuggestions) {
        debouncedSearch(newValue);
      }

      setSelectedIndex(-1);
    },
    [onChange, showSuggestions, debouncedSearch]
  );

  const handleClear = useCallback(() => {
    setLocalValue("");
    onChange("");
    setSuggestions([]);
    setShowSuggestionsDropdown(false);
    setSelectedIndex(-1);
    setError(null);
  }, [onChange]);

  const handleSuggestionClick = useCallback(
    (suggestion) => {
      if (onSuggestionSelect) {
        onSuggestionSelect(suggestion);
      } else {
        // Navigate to product page
        navigate(`/products/${suggestion.slug}`);
      }

      setLocalValue(suggestion.name);
      onChange(suggestion.name);
      setShowSuggestionsDropdown(false);
      setSelectedIndex(-1);
    },
    [onSuggestionSelect, navigate, onChange]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (!showSuggestionsDropdown || suggestions.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case "Enter":
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
            handleSuggestionClick(suggestions[selectedIndex]);
          } else if (onSearchSubmit && localValue.trim()) {
            onSearchSubmit(localValue);
          }
          break;
        case "Escape":
          setShowSuggestionsDropdown(false);
          setSelectedIndex(-1);
          break;
        default:
          break;
      }
    },
    [
      showSuggestionsDropdown,
      suggestions,
      selectedIndex,
      handleSuggestionClick,
      onSearchSubmit,
      localValue,
    ]
  );


  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestionsDropdown(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getMatchTypeIcon = useCallback((matchType) => {
    switch (matchType) {
      case "name":
        return <Search className="h-3 w-3" />;
      case "brand":
        return <Hash className="h-3 w-3" />;
      case "category":
      case "tag":
        return <Tag className="h-3 w-3" />;
      default:
        return <Search className="h-3 w-3" />;
    }
  }, []);

  const getMatchTypeColor = useCallback((matchType) => {
    switch (matchType) {
      case "name":
        return "text-primary-600 bg-primary-50 border-primary-200";
      case "brand":
        return "text-purple-600 bg-purple-50 border-purple-200";
      case "category":
        return "text-green-600 bg-green-50 border-green-200";
      case "tag":
        return "text-orange-600 bg-orange-50 border-orange-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  }, []);

  return (
    <>
      <style>{searchBarStyles}</style>
      <div className={`relative ${className}`} ref={searchRef}>
        {/* Enhanced Search Input Container with Theme Colors */}
        <div
          className={`relative group transition-all duration-300 ${
            isFocused ? "search-input-focus" : ""
          }`}
        >
          {/* Decorative Background Elements */}
          <div className="absolute inset-0 bg-gradient-to-r from-primary-50/30 via-white to-accent-50/30 rounded-2xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-500"></div>

          {/* Search Icon with Enhanced Styling */}
          <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none z-10">
            <div
              className={`p-2 rounded-xl transition-all duration-300 ${
                isFocused
                  ? "bg-primary-100 text-primary-600 shadow-lg"
                  : "text-gray-400 group-hover:text-primary-500"
              }`}
            >
              <Search className="h-5 w-5" />
            </div>
          </div>

          {/* Enhanced Input Field */}
          <input
            type="text"
            value={localValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={placeholder}
            className={`block w-full pl-16 pr-20 py-4 border-2 rounded-2xl bg-white/90 backdrop-blur-sm transition-all duration-300 text-base font-medium font-jakarta placeholder:text-gray-400 focus:outline-none focus:ring-0 focus:border-primary-500 hover:border-primary-300 shadow-sm hover:shadow-md focus:shadow-lg ${
              isFocused
                ? "border-primary-500 bg-white shadow-xl"
                : "border-gray-200 hover:border-gray-300"
            }`}
            autoComplete="off"
          />

          {/* Enhanced Action Buttons with Better Visual Hierarchy */}
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 space-x-2">
            {onSearchSubmit && localValue.trim() && (
              <button
                onClick={() => onSearchSubmit(localValue)}
                className="p-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 shadow-md hover:shadow-lg transform hover:scale-105 active:scale-95"
                type="button"
                title="Search"
              >
                <Search className="h-4 w-4" />
              </button>
            )}

            {localValue && (
              <button
                onClick={handleClear}
                className="p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transform hover:scale-105 active:scale-95"
                title="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Focus Indicator Ring */}
          {isFocused && (
            <div className="absolute inset-0 rounded-2xl ring-2 ring-primary-200 ring-offset-2 pointer-events-none animate-pulse"></div>
          )}
        </div>

        {/* Enhanced Search Suggestions Dropdown with Theme Colors */}
        {showSuggestions && showSuggestionsDropdown && (
          <div
            ref={suggestionsRef}
            className="absolute top-full left-0 right-0 mt-3 bg-white border border-gray-200/50 rounded-2xl shadow-2xl z-50 max-h-[70vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent search-suggestion-enter"
            style={{
              boxShadow:
                "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(236, 72, 153, 0.1)",
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              touchAction: "pan-y"
            }}
          >
            {/* Loading State with Enhanced Design */}
            {loading && (
              <CircularLoader
                size="md"
                variant="primary"
                text="Searching products..."
                showText={true}
              />
            )}

            {/* Error State with Enhanced Design */}
            {error && (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-red-200">
                  <X className="w-8 h-8 text-red-500" />
                </div>
                <p className="text-base font-semibold text-red-600 font-jakarta">
                  {error}
                </p>
                <p className="text-sm text-gray-500 mt-1 font-jakarta">
                  Please try again
                </p>
              </div>
            )}

            {/* Search Results with Enhanced Design */}
            {!loading && !error && suggestions.length > 0 && (
              <div className="py-3">
                {suggestions.map((suggestion, index) => (
                  <div
                    key={suggestion.id}
                    className={`px-6 py-4 cursor-pointer transition-all duration-300 border-l-4 ${
                      index === selectedIndex
                        ? "bg-primary-50/80 border-l-primary-500 shadow-lg transform scale-[1.02]"
                        : "border-l-transparent hover:bg-gray-50/80 hover:border-l-primary-300 hover:shadow-md"
                    }`}
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <div className="flex items-start gap-4">
                      {/* Product Image */}
                      <div className="flex-shrink-0">
                        {suggestion.image ? (
                          <img
                            src={getImageUrl(suggestion.image)}
                            alt={suggestion.name}
                            className="w-14 h-14 rounded-lg object-cover bg-gray-100 border"
                            loading="lazy"
                            onError={(e) => {
                              e.target.src =
                                "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 64 64'%3E%3Crect width='64' height='64' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Inter, Arial, sans-serif' font-size='10' fill='%236b7280'%3ENo Image%3C/text%3E%3C/svg%3E";
                            }}
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-lg bg-gray-100 border flex items-center justify-center">
                            <span className="text-xs text-gray-500">
                              No Image
                            </span>
                          </div>
                        )}
                      </div>
                      {/* Product Details with Enhanced Typography */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h4 className="text-base font-bold text-gray-900 font-jakarta group-hover:text-primary-600 transition-colors leading-tight">
                              {suggestion.name}
                            </h4>
                            {suggestion.brand && (
                              <p className="text-sm text-gray-600 mt-1 font-medium font-jakarta">
                                by {suggestion.brand}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center space-x-3">
                            {/* Enhanced Price Display with MRP */}
                            {typeof suggestion.price === "number" && (
                              <div className="text-right">
                                {suggestion.has_discount &&
                                suggestion.original_price ? (
                                  <div>
                                    <p className="text-sm text-gray-500 line-through font-jakarta">
                                      MRP: ₹
                                      {suggestion.original_price.toFixed(2)}
                                    </p>
                                    <p className="text-lg font-bold text-primary-600 font-jakarta">
                                      ₹{suggestion.price.toFixed(2)}
                                    </p>
                                  </div>
                                ) : (
                                  <p className="text-lg font-bold text-primary-600 font-jakarta">
                                    MRP: ₹{suggestion.price.toFixed(2)}
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Enhanced Match Type Badge */}
                            <span
                              className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border ${getMatchTypeColor(
                                suggestion.matchType
                              )} font-jakarta`}
                            >
                              {getMatchTypeIcon(suggestion.matchType)}
                              <span className="ml-1.5 capitalize">
                                {suggestion.matchType}
                              </span>
                            </span>
                          </div>
                        </div>

                        {/* Enhanced Categories and Tags */}
                        {((suggestion.categories &&
                          suggestion.categories.length > 0) ||
                          (suggestion.tags && suggestion.tags.length > 0)) && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {(suggestion.categories || [])
                              .slice(0, 2)
                              .map((category, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-3 py-1.5 rounded-full text-xs bg-green-100 text-green-800 border border-green-200 font-medium font-jakarta"
                                >
                                  <Tag className="h-3 w-3 mr-1.5" />
                                  {category}
                                </span>
                              ))}
                            {(suggestion.tags || [])
                              .slice(0, 2)
                              .map((tag, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-3 py-1.5 rounded-full text-xs bg-orange-100 text-orange-800 border border-orange-200 font-medium font-jakarta"
                                >
                                  <Hash className="h-3 w-3 mr-1.5" />
                                  {tag}
                                </span>
                              ))}
                          </div>
                        )}
                      </div>

                      {/* Enhanced Arrow Icon */}
                      <div className="flex-shrink-0">
                        <div
                          className={`p-2 rounded-full transition-all duration-300 ${
                            index === selectedIndex
                              ? "bg-primary-100 text-primary-600"
                              : "text-gray-400 group-hover:text-primary-500"
                          }`}
                        >
                          <ArrowRight className="h-5 w-5" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Enhanced No Results State */}
            {!loading &&
              !error &&
              suggestions.length === 0 &&
              localValue.length >= 2 && (
                <div className="p-8 text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-gray-50 to-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-gray-200">
                    <Search className="w-10 h-10 text-gray-400" />
                  </div>
                  <p className="text-base font-semibold text-gray-700 font-jakarta">
                    No products found for "{localValue}"
                  </p>
                  <p className="text-sm text-gray-500 mt-2 font-jakarta">
                    Try different keywords or browse our categories
                  </p>
                  <div className="mt-4 flex justify-center">
                    <button className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-all duration-300 font-medium font-jakarta transform hover:scale-105">
                      Browse Categories
                    </button>
                  </div>
                </div>
              )}
          </div>
        )}
      </div>
    </>
  );
};

export default SearchBar;
